// @flow
import { useEffect, useMemo, } from 'react';
import { useViewResource, useResourceFetch, } from '../../hoc/ViewResource';
import { ResourceCloudIpEdit, ResourceCloudIpView, useEditResourceCloudIps } from '../../common/ResourceCloudIps';
import { ResourceAddViewRoute } from '../../element/ResourceAddViewRoute';
import { Panel, PanelHeader, PanelMultiSettingsBar } from '../../element/Panel';
import { useResourceLockDialogs, useDeleteDialog, LockDialogs, DeleteDialog, SnapshotCreatingDialog, CreateSnapshotDialog } from '../../common/CommonDialogs';
import { useResourceEditorModal } from '../../common/Editor';
import { ResourceServerGroupView, } from '../../common/ServerGroupSelector';
import { ServerServerGroupSelector } from './edit/ServerServerGroupSelector';
import { NameEditPanel } from '../../element/NameEditPanel';
import { useDialog, Dialog } from '../../element/Dialog';
import { useDispatch, useSelector } from 'react-redux';
import { SkeletonBar, SkeletonChip } from '../../element/Skeleton';
import { ResourceChip } from '../../element/Chip';
import { Tooltip } from '../../element/Tooltip';
import { Pill } from '../../element/Pill';
import { getMonthlyPrice } from '../../../lib/pricing';
import { getLockSetting } from '../../element/Setting/Settings';
import { UserDataEditor, UserDataSummary } from './UserDataEditor';
import { validateUserData } from './edit/lib';
import { snapshotDisplay, SnapshotEdit, ServerSnapshotHint } from '../../common/SnapshotEdit';
import { formatUtcDateTime, Bar } from '../../element/Styled';
import { SnapshotRetentionEdit, DEFAULT_RETENTIONS } from '../../common/SnapshotRetentionEdit';
import { isAllocated, isDeleted } from '../../../api/lib';
import { useBuildingResource } from '../../hoc/Build';
import { Link } from 'react-router-dom';
import { Button } from '../../element/Button';
import { TypeSummary } from '../../element/TypeSummary';
import { MonthlyPrice } from '../../element/MonthlyPrice';
import { isWindowsImage } from '../../../api/type.srv';
import { useServerResize } from '../../hoc/ResizeResource';
import { useServerAttachVolumeEditor, useVolumeResizeEditor } from '../../hoc/Volumes';
import { ServerTypeEdit } from './edit/ServerTypeEdit';
import { useCurrentAccount } from '../../hoc/lib';
import { Notice } from '../../element/Notice';
import { ServerVolumeView } from './ServerVolumeView';
import { ServerAttachVolumeEdit } from './edit/ServerAttachVolumeEdit';
import { VolAttachedDialog } from '../volume/VolDialogs';
import { VolumeResizeEdit } from './edit/VolumeResizeEdit';
import { useRouteMatch } from 'react-router';
import { ResourceLink } from '../../element/ResourceLink';
import { useSnapshot } from '../../hoc/Snapshot';
import { ServerMetrics } from '../../common/Metrics/ServerMetrics';
import { LABELS } from '../../element/ResourceLabels';

import type { Match } from 'react-router-dom';
import type { Dispatch } from 'redux';
import type { ViewResourceProps } from '../../hoc/ViewResource';
import type { PanelHeaderActions } from '../../element/Panel';
import type { LockDialogsHook, DeleteDialogHook } from '../../common/CommonDialogs';
import type { EditorDef, EditorModal } from '../../common/Editor';
import type { CloudGuiState } from '../../../state/cloudgui';
import type { DialogState } from '../../element/Dialog';
import type { ResourceActionMethod, ResourceServerConsoleAction, ResourceCollection } from '../../../state/resource/type';
import type { SnapshotRetentionValues } from '../../common/SnapshotRetentionEdit';
import type { BbServer, BbServerParams, UserDataEdit, } from '../../../api/type.srv';
import type { BbImage, BbServerType } from '../../../api/type';
import type { MessageHook } from '../../hoc/Messages';
import type { BbVolume, BbNestedVolume } from '../../../api/type.volume';
import type { EditGigabytes } from '../../hoc/Volumes';
import type { Megabytes } from '../../../api/type.units';
import type { SnapshotHook } from '../../hoc/Snapshot';
import type { ServerTypeValue } from '../../hoc/ResizeResource';


type ServerViewResourceProps = ViewResourceProps<BbServer, BbServerParams>;

const nameDef: EditorDef<BbServer, string, BbServerParams, null> = {
    emptyErrors: null,
    editUri: 'name',
    onEdit: (res: BbServer) => res.name || '',
    onValidate: null,
};

const user_dataDef: EditorDef<BbServer, UserDataEdit, BbServerParams, ?string> = {
    emptyErrors: null,
    editUri: 'user_data',
    onEdit: (server: BbServer): UserDataEdit => {
        let result: UserDataEdit= {
            value: server.user_data || '',
            isBase64Encoded: false,
        };
        if (server.user_data != null && server.user_data !== '') {
            try {
                result = { value: atob(server.user_data), isBase64Encoded: false };
            } catch (err){
            }
        }
        return result;
    },
    onValidate: (r, v) => validateUserData(v),
};

const server_groupsDef: EditorDef<BbServer, $ReadOnlyArray<string>, BbServerParams, null> = {
    emptyErrors: null,
    editUri: 'server_groups',
    onEdit: (server: BbServer) => server.server_groups.map(g => g.id),
    onValidate: (resource: ServerViewResourceProps, value: $ReadOnlyArray<string>) =>
        [null, { server_groups: value }]
    ,
}

const snapshots_scheduleDef: EditorDef<BbServer, string, BbServerParams, null> = {
    emptyErrors: null,
    editUri: 'snapshots_schedule',
    onEdit: (server: BbServer) => server.snapshots_schedule || '',
    onValidate: null,
}

const snapshots_retentionDef: EditorDef<BbServer, SnapshotRetentionValues, BbServerParams, ?string> = {
    emptyErrors: null,
    editUri: 'snapshots_retention',
    onEdit: (server: BbServer) => ({
        automatic: Number(server.snapshots_retention) > 0,
        count: server.snapshots_retention || DEFAULT_RETENTIONS,
    }),
    onValidate: (resource: ServerViewResourceProps, value: SnapshotRetentionValues): [?string, ?$Shape<BbServerParams>] => {
        if (value.automatic && (isNaN(value.count) || Number(value.count) <= 0)) {
            return ['You must retain at least 1 backup', null];
        }

        return [null, { snapshots_retention: value.automatic ? value.count : '' } ];
    },
}

type SrvDialogsHook = {
    actions: PanelHeaderActions,
    ...LockDialogsHook,
    ...DeleteDialogHook,
    stopDialog: DialogState<null>,
    shutdownDialog: DialogState<null>,
    rebootDialog: DialogState<null>,
    resetDialog: DialogState<null>,
}

type SrvDialogsProps = {
    +name: string,
    +hook: SrvDialogsHook,
    +snapshot: SnapshotHook<BbServer>,
    +isNbsServer: boolean,
    +deleteWithServerVolumes: $ReadOnlyArray<BbNestedVolume>,
    +messages: ?MessageHook<BbServer>,
    +attachedDialog: DialogState<BbVolume>,
    +serverTypeChangedDialog: DialogState<ServerTypeValue<BbServerType>>,
}

const useSrvDialogs = (
    id: string, server: ?BbServer,
    simpleAction: (action: string, params?: ?$Shape<BbServerParams>, nonPristine: ?BbServer, method?: ?ResourceActionMethod) => void,
    deleteAction: () => void
): SrvDialogsHook => {

    const allocated = server?.status === 'active' || server?.status === 'inactive';
    const { panelActions: lockActions, ...lockDialogs } = useResourceLockDialogs(allocated ? server?.locked : null, simpleAction);
    const [deleteServerAction, deleteDialog] = useDeleteDialog(allocated && !server?.locked, deleteAction);

    let actions: PanelHeaderActions = {
        powerOff: null,
        activate_console: null,
        start: null,
        snapshot: null,
    };

    const stopDialog = useDialog([ { label: 'Stop', kind: 'primary', onSelect: () => simpleAction('stop'), } ]);
    const shutdownDialog = useDialog([ { label: 'Shutdown', kind: 'primary', onSelect: () => simpleAction('shutdown'), } ]);
    const rebootDialog = useDialog([ { label: 'Reboot', kind: 'primary', onSelect: () => simpleAction('reboot'), } ]);
    const resetDialog = useDialog([ { label: 'Reset', kind: 'primary', onSelect: () => simpleAction('reset'), } ]);
    const dispatch = useDispatch<Dispatch<ResourceServerConsoleAction>>();

    if (allocated) {
        if (server?.status === 'active') {
            actions['powerOff'] = {
                stop: stopDialog.show,
                shutdown: shutdownDialog.show,
                reset: resetDialog.show,
                reboot: rebootDialog.show,
            };
            actions['activate_console'] = () => {
                dispatch({
                    type: 'RESOURCE_SERVER_CONSOLE',
                    payload: { id, }
                });
            }
        } else {
            actions['start'] = () => {
                simpleAction('start');
            };
        }
    }

    return {
        ...lockDialogs,
        actions: {
            ...actions,
            ...deleteServerAction,
            ...(allocated ? lockActions : null)
        },
        deleteDialog,
        stopDialog,
        shutdownDialog,
        rebootDialog,
        resetDialog,
    };
}


const SrvDialogs = ({name, hook, snapshot, messages, isNbsServer, deleteWithServerVolumes, attachedDialog, serverTypeChangedDialog, }: SrvDialogsProps): React$Node => {
    return (
        <>
            <LockDialogs name={name} lockDialog={hook.lockDialog} unlockDialog={hook.unlockDialog} />
            <DeleteDialog
                name={name}
                dialog={hook.deleteDialog}
                render={() => (
                    <>
                        <p>Are you sure you want to delete {name}?</p>
                        {deleteWithServerVolumes.length
                            ? <Notice type='warning' icon='warning-fill'>
                                <p>This Cloud Server has Volumes ({deleteWithServerVolumes.map(({ id, name }, index) => <>{index ? ', ' : ''}<ResourceLink id={id} /></>)})
                                currently configured as <b className='mx-1'><code>delete_with_server</code></b> which means
                                they will also be permanently deleted along with the server.</p>
                            </Notice>
                            : null
                        }
                    </>
                )}

            />
            <VolAttachedDialog attachedDialog={attachedDialog} />

            <Dialog
                title='Send Stop Command?'
                dialog={hook.stopDialog}
                messages={messages}
                render={() =>
                    <>
                        <p>Please confirm you want to stop: {name}</p>
                        {!isNbsServer
                            ? <Notice icon='info-fill' size='sm'><b>Note</b> – billing will continue while the server is stopped</Notice>
                            : null
                        }
                    </>
            } />
            <Dialog
                title='Send Shutdown Command?'
                dialog={hook.shutdownDialog}
                messages={messages}
                render={() =>
                    <>
                        <p>Please confirm you want to shutdown: {name}</p>
                        {!isNbsServer
                            ? <Notice icon='info-fill' size='sm'><b>Note</b> – billing will continue while the server is shut down</Notice>
                            : null
                        }
                    </>
            } />
            <Dialog
                title='Send Reboot Command?'
                dialog={hook.rebootDialog}
                messages={messages}
                render={() =>
                <p>
                    Please confirm you want to reboot: {name}<br/>
                </p>
            } />
            <Dialog
                title='Send Reset Command?'
                dialog={hook.resetDialog}
                messages={messages}
                render={() =>
                <p>
                    Please confirm you want to reset: {name}<br/>
                </p>
            } />
            <CreateSnapshotDialog
                name={name}
                dialog={snapshot.snapshotDialog}
                messages={snapshot.messages}
            />
            <SnapshotCreatingDialog
                name={name}
                dialog={snapshot.snapshotDetailsDialog}
            />
            <Dialog
                title='Cloud Server Resized'
                dialog={serverTypeChangedDialog}
                footerLink={<a href='https://www.brightbox.com/docs/guides/control-panel/resize-cloud-server/' rel='noreferrer noopener' target='blank'>
                <Button size='sm' kind='bare' preIcon='info-fill'>Resizing Cloud Servers</Button></a>}
                render={(value) => (
                    <>
                        <p><b>{name}</b> has been resized.</p>

                        {value && value.selected && value.selected?.ram > value.current.ram
                            ? <Notice type='info' icon='info-fill'>
                                <p><b>Note</b> – increased {isNbsServer ? 'RAM and CPU' : 'RAM, CPU and local SSD'} allocations
                                are "hot plugged" and in most cases can be <a href='https://www.brightbox.com/docs/guides/control-panel/resize-cloud-server/#accessing-new-resources' rel='noopener noreferrer' target='_blank'>accessed straightaway without restarting</a></p>

                                <p>Alternatively, shutting-down and starting a Cloud Server will force it to update to the new resource allocations.</p>
                            </Notice>
                            : null
                        }

                        {value && value.selected && value.selected?.ram < value.current.ram
                            ? <Notice type='info' icon='info-fill'>
                                The Cloud Server will pick up the updated RAM and/or 
                                CPU allocations when it is started again.
                            </Notice>
                            : null
                        }
                    </>
                )}
            />
        </>
    );
}

function useServerSshConnection(server: ?BbServer, fullImage: ?BbImage): [?string, React$Node] {
    if (server == null) return [null, null];

    if (server?.image?.name?.startsWith('Windows 2008')) {
        return [null, null];
    }

    let username = fullImage?.username || server?.image?.username || fullImage?.ancestor?.username || '';
    if (username !== '') username += '@';

    if (server.cloud_ips.length === 0) {
        const connection = username + 'ipv6.' + server.fqdn;

        return [
            'ssh ' + connection,
            <>
               <span className='select-all'>ssh {connection}</span>
                <Tooltip
                    overlay={
                        <>
                            <p>This Cloud Server doesn't currently have any Cloud IPs so
                                can only be accessed via static IPv6.</p>
                            <p>Map a Cloud IP to enable public IPv4 access.</p>
                            <Link to='cloud_ips/'><Button size='sm' className='my-2' color='white'>Map a Cloud IP</Button></Link>
                        </>
                    }>
                    <Pill>IPv6</Pill>
                </Tooltip>
            </>
        ];
    }

    const connection = username + 'public.' + server.fqdn;

    return [
        'ssh ' + connection,
        <div className='select-all'>ssh {connection}</div>
    ];

}

function useAvailableMemoryForServerResize(server: ?BbServer): number {
    const account = useCurrentAccount();

    if (!account || !server) return 0;

    return account.ram_limit - account.ram_used + server.server_type.ram;
}

function useAttachedVolumeResize(match: Match): [EditorModal<EditGigabytes, ?string, BbVolume>, ?Megabytes, ?React$Node] {
    const volPath = 'volume_resize/:volId(vol-[a-z0-9]{5})/';
    const volMatch = useRouteMatch(match.path + volPath);
    //const id =
    const id = volMatch?.params?.['volId'];
    const { item: volume, fetch } = useResourceFetch('volume', id);
    useEffect(() => {
        if (id != null) fetch();
    }, [id, fetch]);

    return useVolumeResizeEditor(match.path, volume, volPath);
}

function useDeleteWithServerVolumes(server: ?BbServer): $ReadOnlyArray<BbNestedVolume> {
    const volumes = useSelector<CloudGuiState, ResourceCollection<BbVolume, BbVolume>>(state => state.Resource.volume);

    return useMemo(() => server
        ? server.volumes.filter((nested: BbNestedVolume) => {
            const volume: BbVolume | BbNestedVolume = (volumes.full[nested.id] || volumes.collected[nested.id] || nested);
            return volume.storage_type === 'network' && volume.delete_with_server;
        })
        : [],
        [server, volumes]
    )
}

const NEW_CONFIG_START_EPOCH = new Date('April 20, 2022').getTime();

export const ServerView = ({ id, match }: { id: string, match: Match }): React$Node => {
    const resource = useViewResource<BbServer, BbServerParams>('server', id);
    const { item: server, } = resource;
    const cloudIp = useEditResourceCloudIps(id, match.path, server?.id || '');
    const progress = useBuildingResource(id);

    const hasCreatingSnapshot = !!server?.snapshots?.find(x => x.status === 'creating');
    const dialogs = useSrvDialogs(
        id, server,
        resource.simpleAction, resource.deleteAction
    );
    const isNotDeleted = !isDeleted(server?.status);
    const allocated = isAllocated(server?.status);
    const snapshot = useSnapshot(
        () => resource.simpleAction('snapshot'),
        resource.apiResult, hasCreatingSnapshot, allocated
    );

    const name = useResourceEditorModal(resource, nameDef, match.path);
    const user_data = useResourceEditorModal(resource, user_dataDef, match.path);
    const server_groups = useResourceEditorModal(resource, server_groupsDef, match.path);
    const snapshots_schedule = useResourceEditorModal(resource, snapshots_scheduleDef, match.path);
    const snapshots_retention = useResourceEditorModal(resource, snapshots_retentionDef, match.path);
    const { editor: server_type, onValidate: onTypeValidate, serverTypeChangedDialog } = useServerResize(
        match.path, server
    );
    const availableMemory = useAvailableMemoryForServerResize(server);

    const { item: fullImage, } = useViewResource('image', server?.image?.id || '');
    const [sshValue, sshSummary] = useServerSshConnection(server, fullImage);

    const isNbsServer = (server?.server_type?.storage_type === 'network');
    const attach = useServerAttachVolumeEditor(server, match.path);

    const [resizeEditor, originalSize,] = useAttachedVolumeResize(match);

    const deleteWithServerVolumes = useDeleteWithServerVolumes(server);
    const hasNewConfig: boolean = (server?.started_at != null && (server.started_at.getTime() >= NEW_CONFIG_START_EPOCH));

    return (
        <ResourceAddViewRoute
            listTitle={LABELS.server.listTitle}
            resourceName={server?.name || server?.id || ''}
            resource={resource}
            match={match}
            dialog={
                <SrvDialogs
                    name={server?.name || id}
                    isNbsServer={isNbsServer}
                    deleteWithServerVolumes={deleteWithServerVolumes}
                    hook={dialogs}
                    snapshot={snapshot}
                    messages={resource.apiResult}
                    attachedDialog={attach.attachedDialog}
                    serverTypeChangedDialog={serverTypeChangedDialog}
                />}
            view={
                <>
                    <Panel>
                        <PanelHeader
                            cacheStatus={resource.status}
                            title={server?.name || server?.id}
                            actions={{
                                ...dialogs.actions,
                                ...snapshot.actions,
                            }}
                            disableSnapshotHint={
                                hasCreatingSnapshot
                                    ? 'Snapshot in progress'
                                    : null
                            }
                            editTitleLink={isNotDeleted ? name.editUri : null}
                        />
                        <PanelMultiSettingsBar
                            cacheStatus={resource.status}
                            details={{
                                id: server?.id,
                                status: server?.status,
                                progress,
                                kind: 'server',
                                created_at: server?.created_at,
                            }}
                            startServer={dialogs.actions.start}
                            settings={[
                                [
                                {
                                    name: 'Image',
                                    summary: server
                                        ? <ResourceChip resource={server?.image}/>
                                        : null,
                                    skeleton: <SkeletonChip />,
                                },
                                {
                                    name: 'Server Type',
                                    summary: server
                                        ? <>
                                            {server.server_type.handle
                                                ? <b className='text-gray-700 font-mono'>{ server.server_type.handle }</b>
                                                : <b className='text-gray-700'>{server.server_type.name || server.server_type.id}</b>
                                            }
                                            <Bar/>
                                            <TypeSummary
                                                cpus={server.server_type.cores}
                                                ram={server.server_type.ram}
                                                disk={server.server_type.disk_size}
                                            />
                                        </>
                                        : null,
                                    skeleton: <SkeletonBar size='xs'/>,
                                    ...(isAllocated(server?.status)
                                        ? {
                                            route: server_type.editUri,
                                            settingsBtn: {
                                                kind: 'secondary',
                                                children: 'Resize',
                                            },
                                            routeDisabledTooltip:
                                                    !hasNewConfig
                                                    ? <>
                                                        <p>This server is currently running an older config
                                                        which predates the Resize feature.</p>
                                                        <p>You can update the server to latest config by shutting down
                                                        and re-starting the server.</p>
                                                    </>
                                                    : null
                                        }
                                        : null
                                    ),
                                },
                                    {
                                        name: 'Cost',
                                        summary: (
                                            <MonthlyPrice
                                                price={getMonthlyPrice(server?.server_type?.id || '')}
                                                plusWindows={server?.image && isWindowsImage(server.image)}
                                            />
                                        )
                                    }
                                ],
                                [
                                    {
                                        name: 'Private IPv4',
                                        summary: <span className='select-all'>{server?.interfaces?.[0]?.ipv4_address}</span>,
                                        copyValue: server?.interfaces?.[0]?.ipv4_address,
                                        hint: 'The private IPv4 address can be used within the Brightbox local network for server to server networking without incurring internet data charges'
                                    },
                                    {
                                        name: 'Static IPv6',
                                        summary: <span className='select-all'>{server?.interfaces?.[0]?.ipv6_address}</span>,
                                        copyValue: server?.interfaces?.[0]?.ipv6_address,
                                        skeleton: <SkeletonBar size='lg' />,
                                        hint: 'The static IPv6 address can be used to access a Cloud Server remotely via IPv6 without a Cloud IP',
                                    },
                                    {
                                        name: 'SSH Connection',
                                        summary: sshSummary || 'n/a',
                                        copyValue: sshValue,
                                    },
                                ],
                                [
                                    {
                                        name: 'Zone',
                                        summary: server?.zone?.handle,
                                        skeleton: <SkeletonBar size='sm' />,
                                    },
                                    {
                                        name: 'User Data',
                                        summary: server
                                            ? <UserDataSummary encoded={server.user_data} />
                                            : null,
                                        route: isNotDeleted ? user_data.editUri : null,
                                    },
                                    getLockSetting(!!server && isAllocated(server.status), server?.locked, dialogs.unlockDialog.show, dialogs.lockDialog.show),
                                ],
                                [
                                    {
                                        name: 'Snapshot Schedule',
                                        summary: server
                                            ? snapshotDisplay(server.snapshots_schedule) + (server.snapshots_schedule_next_at ? ` (next at ${formatUtcDateTime(server.snapshots_schedule_next_at)})` : '')
                                            : null,
                                        route: isNotDeleted ? snapshots_schedule.editUri : null,
                                        hint: <ServerSnapshotHint/>,
                                    },
                                    {
                                        name: 'Snapshot Retention',
                                        summary: server?.snapshots_retention != null
                                            ? 'Keep the  newest ' + Number(server.snapshots_retention) + ' snapshots'
                                            : 'Keep all automated snapshots',
                                        route: isNotDeleted ? snapshots_retention.editUri : null,
                                        hint: <div>The number of automated snapshots to be kept (older snapshots will be deleted)</div>,
                                    },
                                ]
                            ]}
                        />
                    </Panel>

                    <ServerMetrics id={id} />

                    <ServerVolumeView resource={resource}/>

                    <ResourceCloudIpView
                        cacheStatus={resource.status}
                        resourceId={id}
                        resourceTitle={'Cloud Server'}
                        editUri={allocated || server?.status === 'creating' ? cloudIp.editor.editUri : null}
                    />

                    <ResourceServerGroupView
                        cacheStatus={resource.status}
                        groups={server?.server_groups || []}
                        editUri={allocated ? server_groups.editUri : null}
                    />
                </>
            }
            editors={[
                {
                    editor: cloudIp.editor,
                    render: () =>
                        <ResourceCloudIpEdit
                            resourceId={id}
                            resourceTitle={'Cloud Server'}
                            edit={cloudIp}
                        />,
                },
                { editor: server_groups, component: ServerServerGroupSelector, },
                { editor: name, render: () => <NameEditPanel editor={name} />},
                { editor: user_data, component: UserDataEditor },
                { editor: snapshots_schedule, render: () => <SnapshotEdit editor={snapshots_schedule} kind='server' />, },
                { editor: snapshots_retention, component: SnapshotRetentionEdit, },
                {
                    editor: server_type,
                    render: () => server
                        ? <ServerTypeEdit
                            editor={server_type}
                            selectedImage={fullImage}
                            availableMemory={availableMemory}
                            name={server.name || server.id || 'this server'}
                            id={id}
                            onValidate={onTypeValidate}
                            isInactive={server.status === 'inactive'}
                            storageTypeFilter={server.server_type.storage_type}
                        />
                        : null
                    ,
                },
                { editor: attach.editor, render: () => (
                    <ServerAttachVolumeEdit
                        editor={attach.editor}
                        confirmDialog={attach.confirmDialog}
                        serverId={id}
                        serverZoneId={server?.zone?.id || ''}
                        title='Attach Volume'
                        description='Choose a Volume to attach to this Cloud Server'
                    />
                ), },
                { editor: resizeEditor, render: () => <VolumeResizeEdit editor={resizeEditor} originalSize={originalSize} /> }
            ]}
        />
    );
};
